<template>
  <div
    class="site-profile-form"
    @change="formUtils.setDirty()"
  >
    <InputField
      :label="$t('settings.site.name')"
      :showFlag="true"
      :showColon="false"
      :required="true"
      :showEmptyDash="true"
      :value="displaySite.code"
      :readonly="true"
      @updateValue="val => updateField('code', val)"
    />
    <InputField
      :label="$t('settings.site.description')"
      :showFlag="true"
      :showColon="false"
      :required="true"
      :value="displaySite.note"
      :readonly="!isEdit"
      @updateValue="val => updateField('note', val)"
    />
    <div class="new-multiselect">
      <label :class="{ emphasis: true, 'required-flag': isEdit}">{{ $t('settings.site.type') }}</label>
      <HKLSelect
        v-if="isEdit"
        :value="displaySite.type && displaySite.type.toUpperCase()"
        :options="siteTypeHklOptions"
        :dirtyCheck="true"
        :required="true"
        :showSearch="true"
        placeholder=""
        @change="val => updateType(val)"
      />
      <span v-else>{{ customSiteTypeLabel(displaySite.type) }}</span>
    </div>
    <label :class="{ emphasis: true, 'required-flag': isEdit}">{{ $t('settings.site.companyName') }}</label>
    <TextField
      v-if="isEdit"
      :value="displaySite.subsidiary"
      :showColon="false"
      :required="true"
      @inputValue="val => updateField('subsidiary', val)"
    />
    <div
      class="clearfix"
      v-html="displaySite.subsidiary || $t('emptyValue')"
    />
    <div class="new-multiselect">
      <label :class="{ emphasis: true, 'required-flag': isEdit}">{{ $t('settings.site.buildings') }}</label>
      <HKLMultiSelect
        v-if="isEdit"
        :value="buildingCodes(updatedSite)"
        :class="{ 'multi-value': true, 'error-highlight': isBuildingsEmpty('buildings') && checkState.buildings }"
        :options="buildingHklOptions"
        placeholder=""
        :dirtyCheck="true"
        :showSearch="false"
        :showLabels="false"
        :required="true"
        @change="updateSiteBuildings"
      />
      <span v-else>{{ showBuildings(updatedSite) }}</span>
    </div>
    <SiteContacts
      v-show="isPhysicalSite"
      ref="siteContacts"
      :key="siteDetail.id"
      class="location-info u-sec-margin-bottom"
      :isEditable="isEdit"
      :users="users"
    />
    <TextField
      v-show="isPhysicalSite"
      :showColon="false"
      class="location-info__location"
      :label="$t('settings.building.location')"
      :readonly="!isEdit"
      :defaultEmpty="true"
      :value="displaySite.officeLocation"
      @updateValue="val => updateField('officeLocation', val)"
    />
    <InputField
      v-show="isPhysicalSite"
      :showFlag="false"
      :showColon="false"
      class="location-info__telephone"
      extendClass="location-info__telephone--input"
      :label="$t('settings.building.telephone')"
      :readonly="!isEdit"
      :showEmptyDash="true"
      :value="displaySite.officeTelephone"
      @updateValue="val => updateField('officeTelephone', val)"
    />
    <hr>
    <div class="signatures">
      <label class="emphasis">{{ $t('settings.tab.tenantSignature') }}</label>
      <div
        v-for="(signature, index) in displaySite.templates"
        :key="index"
        class="signature"
      >
        <span
          v-if="isEdit"
          class="delete-icon"
          @click="deleteSignature(index)"
        >&#x2715;</span>
        <InputField
          :label="`${$t('settings.site.signature')} ${$t('name')}`"
          :value="signature.title"
          :readonly="!isEdit"
          :showColon="false"
          :required="true"
          @updateValue="val => updateSignature('title', val, index)"
        />
        <div class="new-multiselect">
          <label :class="{ emphasis: true, 'required-flag': isEdit }">{{ $t('settings.site.buildings') }}</label>
          <HKLMultiSelect
            v-if="isEdit"
            :value="buildingCodes(signature)"
            :class="{ 'multi-value': true, 'error-highlight': isBuildingsEmpty('signatureBuildings', index) && checkState.signatureBuildings[index] }"
            :options="signatureBuildingHklOptions"
            placeholder=""
            :dirtyCheck="true"
            :showSearch="false"
            :showLabels="false"
            :required="true"
            @change="val => updateSignatureBuildingCodes(index, val)"
          />
          <span v-else>{{ showBuildings(signature) }}</span>
        </div>
        <label :class="{ emphasis: true, 'required-flag': isEdit }">{{ $t('settings.site.signature') }}</label>
        <TextField
          v-if="isEdit"
          :value="signature.content"
          :showColon="false"
          :required="true"
          @inputValue="val => updateSignature('content', val, index)"
        />
        <div
          class="clearfix"
          v-html="signature.content || $t('emptyValue')"
        />
      </div>
      <div
        v-if="isEdit"
        class="add-signature"
        @click="addSignature()"
      >
        {{ $t('settings.site.addSignature') }} +
      </div>
    </div>
  </div>
</template>

<script>
import _, { cloneDeep } from 'lodash'
import { utils as formUtils } from '@/plugin/InputChange'
import InputField from '@/controls/InputField'
import TextField from '@/controls/TextField'
import HKLSelect from '@/controls/Select/HKLSelect.vue'
import HKLMultiSelect from '@/controls/Select/HKLMultiSelect.vue'

import { updateLocation } from '@/api/locations/request'
import { getBuildings } from '@/api/buildings/request'
import { getTemplates } from '@/api/settings/request'
import { templateType } from '@/utils/consts'
import { fusionInputCheckUtil } from '@/plugin/FusionInputCheck'
import { SITE_TYPE } from '@/constants/modeltypes'
import SiteContacts from './SiteContacts'
import { checkMultiSites } from '@/utils/siteUtils'

export default {
  name: 'SiteProfileForm',
  components: {
    HKLSelect,
    HKLMultiSelect,
    InputField,
    TextField,
    SiteContacts,
  },
  props: {
    isEdit: {
      type: Boolean,
      default: () => false,
    },
    site: {
      type: Object,
      default: () => {},
    },
  },
  data () {
    return {
      formUtils,
      buildingOptions: [],
      rawBuildingOpts: [],
      updatedSite: {
        type: '',
      },
      checkState: {
        buildings: false,
        signatureBuildings: [],
      },
      editModeSignatureNum: 0,
      defaultTemplate: null,
    }
  },
  computed: {
    users () {
      return this.site.users
    },
    siteDetail () {
      return this.site.location || {}
    },
    siteTemplates () {
      return this.siteDetail.templates || []
    },
    siteBuildings () {
      return this.siteDetail.buildings || []
    },
    displaySite () {
      const typeVal = this.updatedSite.type || this.siteDetail.type
      const type = this.$options.filters['capitalize'](typeVal)
      return {
        ...this.siteDetail,
        ...this.updatedSite,
        type,
      }
    },
    siteTypeOptions () {
      return Object.values(SITE_TYPE)
    },
    siteTypeHklOptions () {
      return this.siteTypeOptions.map((value) => ({
        name: this.customSiteTypeLabel(value),
        value,
      }))
    },
    buildingOptsForPhysical () {
      const result = this.rawBuildingOpts.filter(item => {
        const hasPhysicalSite = item.sites.some(site => site.type === SITE_TYPE.PHYSICAL && site.code !== this.siteDetail.code)
        return !hasPhysicalSite
      })
      return result
    },
    signatureBuildingOpts () {
      const bOpts = cloneDeep(this.updatedSite.buildings).map(b => {
        delete b.removeDisabled
        return b
      })
      return bOpts
    },
    buildingHklOptions () {
      return this.buildingOptions.map((building) => ({
        name: this.customBuildingLabel(building),
        value: building.code,
      }))
    },
    signatureBuildingHklOptions () {
      return this.signatureBuildingOpts.map((building) => ({
        name: this.customBuildingLabel(building),
        value: building.code,
      }))
    },
    isPhysicalSite () {
      return (this.displaySite.type && this.displaySite.type.toUpperCase() === SITE_TYPE.PHYSICAL)
    },
  },
  watch: {
    site: {
      handler: function siteWatchHandler (nVal, oVal) {
        if (!_.isEqual(nVal, oVal)) {
          const attrPath = 'location.type'
          const nType = _.get(nVal, attrPath)
          this.siteTypeWatchHandler(nType)
        }
      },
      immediate: true,
    },
    'siteDetail.id' () {
      this.reset()
    },
    'displaySite.type': {
      handler: function (newType) {
        this.siteTypeWatchHandler(newType)
      },
      immediate: true,
    },
  },
  async mounted () {
    await this.initBuildingOptions()
    const defaultTemplate = await getTemplates(templateType.ECIRCULAR_TENANT_DEFAULT)
    this.defaultTemplate = defaultTemplate.length ? defaultTemplate[0].content : ''
  },
  methods: {
    async initBuildingOptions () {
      const result = await getBuildings()
      this.rawBuildingOpts = cloneDeep(result)
      result.forEach(checkMultiSites)
    },
    customSiteTypeLabel (value) {
      return value ? this.$t(`settings.site.${value.toLowerCase()}`) : this.$t('emptyValue')
    },
    customBuildingLabel ({ code, fullName }, _, isCurrentOptionLabel) {
      return code
    },
    showBuildings (item) {
      return _.isEmpty(item.buildings)
        ? this.$t('emptyValue')
        : item.buildings.map(({code}) => code).join(', ')
    },
    buildingCodes (item = {}) {
      return (item.buildings || []).map(({ code }) => code)
    },
    buildingByCode (code) {
      return this.rawBuildingOpts.find(building => building.code === code) ||
        this.buildingOptions.find(building => building.code === code)
    },
    updateSignature (key, value, index) {
      this.updatedSite.templates[index][key] = value
    },
    updateSignatureBuildings (val) {
      this.unCheckState('buildings')
      this.displaySite.templates.forEach((template, index) => {
        if (template.buildings.length) {
          this.checkState.signatureBuildings.splice(index, 1, false)
        }
      })
      this.updatedSite.templates = this.updatedSite.templates.map(template => {
        template.buildings = template.buildings.filter(building => building.code !== val.code)
        return template
      })
    },
    updateSiteBuildings (codes = []) {
      const oldBuildings = this.updatedSite.buildings || []
      const removedBuildings = oldBuildings.filter(building => !codes.includes(building.code))
      this.unCheckState('buildings')
      this.updatedSite.buildings = codes.map(code => this.buildingByCode(code)).filter(Boolean)
      removedBuildings.forEach(building => this.updateSignatureBuildings(building))
    },
    updateSignatureBuildingCodes (index, codes = []) {
      this.unCheckState('signatureBuildings', index)
      this.updatedSite.templates[index].buildings = codes
        .map(code => this.signatureBuildingOpts.find(building => building.code === code))
        .filter(Boolean)
    },
    setEditModeSignatureNum () {
      this.editModeSignatureNum = this.updatedSite.templates.length
    },
    deleteSignature (index) {
      this.updatedSite.templates.splice(index, 1)
      if (this.isEdit) {
        if (this.updatedSite.templates.length !== this.editModeSignatureNum) {
          formUtils.setDirty()
        } else {
          formUtils.resetDirty()
        }
      } else {
        if (this.updatedSite.templates.length !== this.siteDetail.templates.length) {
          formUtils.setDirty()
        } else {
          formUtils.resetDirty()
        }
      }
    },
    addSignature () {
      const content = this.updatedSite.templates.length === 0 ? this.defaultTemplate : ''
      this.updatedSite.templates.push({
        locationId: this.siteDetail.id,
        title: '',
        content,
        buildings: [],
      })
      formUtils.setDirty()
    },
    updateField (key, value) {
      const val = typeof value === 'string' ? value.trim() : value
      this.updatedSite = {
        ...this.updatedSite,
        [key]: val,
      }
      this.$forceUpdate()
    },
    updateType (val) {
      this.updateField('type', val)
      if (val.toUpperCase() === SITE_TYPE.PHYSICAL) {
        this.updatedSite.buildings = []
      } else {
        this.updatedSite.buildings = [...this.siteDetail.buildings]
      }
    },
    resetCheckState () {
      this.checkState = {
        buildings: false,
        signatureBuildings: [],
      }
    },
    validate () {
      this.resetCheckState()

      this.checkState.buildings = true
      this.displaySite.templates.forEach(index => {
        this.checkState.signatureBuildings.push(true)
      })
      fusionInputCheckUtil.validateAllFields({allowEmpty: 'none'}, '.site-profile-form')
      const isProfileValidation = fusionInputCheckUtil.isAllFieldsValid('.site-profile-form')
      const isContactsValidation = this.$refs.siteContacts.validate()
      const isSignatureBuildingsValid = this.updatedSite.templates.every(template => template.buildings.length)
      const isAllMultiSelectValid = this.updatedSite.buildings.length && isSignatureBuildingsValid
      return isProfileValidation && isAllMultiSelectValid && isContactsValidation
    },
    isBuildingsEmpty (key, index = 0) {
      if (key === 'buildings') {
        return this.updatedSite.buildings ? this.updatedSite.buildings.length === 0 : false
      } else if (key === 'signatureBuildings') {
        return this.updatedSite.templates ? this.updatedSite.templates[index].buildings.length === 0 : false
      }
    },
    unCheckState (key, index = 0) {
      if (key === 'buildings') {
        this.checkState.buildings = false
      } else if (key === 'signatureBuildings') {
        this.checkState.signatureBuildings[index] = false
      }
    },
    async save () {
      const type = this.displaySite.type
      const locationContacts = this.$refs.siteContacts.getUpdatedContacts()
      const payload = {
        ...this.displaySite,
        type: type ? type.toUpperCase() : null,
        locationContacts,
      }
      await updateLocation(this.siteDetail.id, payload)
      await this.$refs.siteContacts.getContacts()
      await this.initBuildingOptions()
      formUtils.resetDirty()
    },
    async reset () {
      this.updatedSite = {
        buildings: [...this.siteBuildings],
        templates: [...this.siteTemplates],
      }

      this.resetCheckState()
      this.$refs.siteContacts.reset()
      formUtils.resetDirty()
    },
    siteTypeWatchHandler (siteType) {
      if (siteType && siteType.toUpperCase() === SITE_TYPE.PHYSICAL) {
        this.buildingOptions = this.buildingOptsForPhysical
      } else {
        this.buildingOptions = this.rawBuildingOpts
      }
    },
  },
}
</script>

<style lang="scss">
@import "~styles/newMultiselect";
</style>

<style lang="scss" scoped>
.site-profile-form {
  width: 100%;
  padding: 0 24px;
  label.emphasis {
    margin: 15px 0 5px 0;
    font-weight: bold;
  }

  ::v-deep .text-input {
    textarea, input {
      width: 100%;
    }
  }

  .signature {
    position: relative;
    background: #f8f8f8;
    padding: 5px 15px 15px;
    margin-top: 15px;

    .delete-icon {
      position: absolute;
      right: 15px;
      top: 5px;
      cursor: pointer;
      font-size: 24px;
      font-weight: bold;
    }

    ::v-deep textarea {
      min-height: 3em;
    }

    .signature-buildings {
      label {
        display: inline-block;
        margin-right: 15px;
        input {
          position: relative;
          top: 1px;
        }
      }
    }
  }

  .add-signature {
    width: 230px;
    border-radius: 20px;
    color: white;
    background: #6e7a91;
    height: 40px;
    line-height: 40px;
    font-weight: bold;
    text-align: center;
    margin: 20px auto;
    cursor: pointer;
    margin-bottom: 0;
  }
}
</style>
